<template>
    <CModal
        :title="titleComponent"
        :show="showComponent"
        :color="colorComponent"
        :size="sizeComponent || 'lg'"
        @update:show="showComponent = $event"
    >
        <CForm
            :key="formKey"
            ref="sendLeadsToDeliveryCampaignWithTrollingDelivery"
            @submit="
                submitForm(
                    $event,
                    'sendLeadsToDeliveryCampaignWithTrollingDelivery'
                )
            "
        >
            <!-- ===== SECCIÓN 1: Origen de leads ===== -->
            <template v-if="initialLeadIds && !manualOverride">
                <div class="d-flex align-items-center justify-content-between mb-3 px-2 py-2 bg-light rounded">
                    <span>
                        {{ $t('Leads to send') }}: <strong>{{ parsedInitialLeads.length }}</strong>
                    </span>
                    <CButton
                        size="sm"
                        color="secondary"
                        variant="outline"
                        @click="manualOverride = true"
                    >
                        {{ $t('Introducir leads manualmente') }}
                    </CButton>
                </div>
            </template>
            <template v-else>
                <p class="font-weight-bold mb-1">{{ $t('Lead source') }}</p>
                <div class="d-flex mb-3">
                    <div class="mr-4">
                        <label
                            class="d-flex align-items-center"
                            :style="leads.length > 0 ? 'cursor:pointer' : 'cursor:not-allowed; opacity:0.5'"
                        >
                            <input
                                type="radio"
                                v-model="leadsSource"
                                value="selected"
                                class="mr-2"
                                :disabled="leads.length === 0"
                            />
                            {{ $t('Use selected') }}
                        </label>
                    </div>
                    <div>
                        <label class="d-flex align-items-center" style="cursor:pointer">
                            <input
                                type="radio"
                                v-model="leadsSource"
                                value="manual"
                                class="mr-2"
                            />
                            {{ $t('Enter IDs manually') }}
                        </label>
                    </div>
                </div>
                <div v-if="leadsSource === 'manual'" class="form-group mb-3">
                    <label class="font-weight-bold">{{ $t('Lead IDs (comma-separated)') }}</label>
                    <textarea
                        v-model="manualLeadIds"
                        class="form-control form-control-sm"
                        rows="3"
                        :placeholder="$t('e.g. 2,3,4,5,6')"
                    ></textarea>
                    <small class="text-muted">{{ parsedManualLeads.length }} {{ $t('IDs detected') }}</small>
                </div>
            </template>

            <hr class="my-3" />

            <!-- ===== SECCIÓN 2: Destino ===== -->
            <p class="font-weight-bold mb-1">{{ $t('Destination') }}</p>
            <div class="d-flex mb-3">
                <div class="mr-4">
                    <label class="d-flex align-items-center" style="cursor:pointer">
                        <input
                            type="radio"
                            v-model="destinationType"
                            value="campaign"
                            class="mr-2"
                        />
                        {{ $t('Direct campaign') }}
                    </label>
                </div>
                <div>
                    <label class="d-flex align-items-center" style="cursor:pointer">
                        <input
                            type="radio"
                            v-model="destinationType"
                            value="balancer"
                            class="mr-2"
                        />
                        {{ $t('Balancer') }}
                    </label>
                </div>
            </div>

            <!-- Campaña directa -->
            <template v-if="destinationType === 'campaign'">
                <SelectAsync
                    :label="$t('Campaign')"
                    :placeholder="$t('Select a campaign')"
                    :optionsValue="'id'"
                    :optionsLabel="'name'"
                    size="sm"
                    endpoint="campaigns.api"
                    :isValid="
                        checkIfFormValid({
                            formName: 'sendLeadsToDeliveryCampaignWithTrollingDelivery',
                            fieldName: 'deliveryCampaignId',
                        })
                    "
                    :value.sync="
                        $v.forms.sendLeadsToDeliveryCampaignWithTrollingDelivery['deliveryCampaignId'].$model
                    "
                />
            </template>

            <!-- Balanceador -->
            <template v-if="destinationType === 'balancer'">
                <div class="form-group">
                    <label class="font-weight-bold col-form-label-sm">{{ $t('Balancer group') }}</label>
                    <select
                        class="form-control form-control-sm"
                        v-model="$v.forms.sendLeadsToDeliveryCampaignWithTrollingDelivery['clientGroupId'].$model"
                    >
                        <option value="" disabled selected>{{ $t('Select a balancer') }}</option>
                        <option
                            v-for="g in clientGroupOptions"
                            :key="g.value"
                            :value="g.value"
                        >{{ g.text }}</option>
                    </select>
                    <div v-if="clientGroupOptions.length === 0" class="text-muted small mt-1">
                        <CSpinner size="sm" class="mr-1" /> {{ $t('Loading...') }}
                    </div>
                </div>
            </template>

            <hr class="my-3" />

            <!-- ===== SECCIÓN 3: Configuración ===== -->
            <p class="font-weight-bold mb-2">{{ $t('Configuration') }}</p>

            <SelectAsync
                :label="'TimeZone'"
                :horizontal="inputHorizontal"
                :options="['UTC', 'Europe/Madrid', 'Europe/Rome']"
                :value.sync="
                    forms.sendLeadsToDeliveryCampaignWithTrollingDelivery.timeZone
                "
                :placeholder="$t('Select a timezone')"
            />

            <CCRange
                :value.sync="
                    forms.sendLeadsToDeliveryCampaignWithTrollingDelivery.scheduleBias
                "
                :label="$t('Frequency')"
                :min="0.1"
                :max="1"
                :step="0.1"
                type="range"
                :horizontal="inputHorizontal"
                size="sm"
            />

            <CInput
                :isValid="
                    checkIfFormValid({
                        formName: 'sendLeadsToDeliveryCampaignWithTrollingDelivery',
                        fieldName: 'maxConcurrency',
                    })
                "
                :value.sync="
                    $v.forms.sendLeadsToDeliveryCampaignWithTrollingDelivery.maxConcurrency.$model
                "
                type="number"
                :label="$t('Max concurrency')"
                :placeholder="$t('Enter a max concurrency possible')"
                :invalidFeedback="$t('Requiered a number')"
                :horizontal="inputHorizontal"
                size="sm"
            />

            <CInput
                :isValid="
                    checkIfFormValid({
                        formName: 'sendLeadsToDeliveryCampaignWithTrollingDelivery',
                        fieldName: 'minConcurrency',
                    })
                "
                :value.sync="
                    $v.forms.sendLeadsToDeliveryCampaignWithTrollingDelivery.minConcurrency.$model
                "
                type="number"
                :label="$t('Min concurrency')"
                :placeholder="$t('Enter a number min concurrency possible')"
                :invalidFeedback="$t('Requiered a number')"
                :horizontal="inputHorizontal"
                size="sm"
            />

        </CForm>
        <template #footer>
            <CButton color="secondary" @click="closeModal()">
                {{ $t("cancel") }}
            </CButton>
            <ButtonLoading
                color="primary"
                @click="
                    executeSubmitForm(
                        'sendLeadsToDeliveryCampaignWithTrollingDelivery'
                    )
                "
                :loading="
                    forms.sendLeadsToDeliveryCampaignWithTrollingDelivery._loading
                "
            >
                {{ $t("send") }}
            </ButtonLoading>
        </template>
    </CModal>
</template>

<script>
/** Components */
import SelectAsync from "~/components/elements/SelectAsync.vue";
import ButtonLoading from "~/components/ButtonLoading.vue";
import CCRange from "~/components/CCRange.vue";

/** Mixins */
import formMixin from "~/mixins/form";
import modalComponentMixin from "~/mixins/modalComponent";

/** Others */
import { validationMixin } from "vuelidate";
import { required, requiredIf } from "vuelidate/lib/validators";

export default {
    name: "CCSendLeadsToDeliveryCampaignWithThrollingDeliveryModal",
    mixins: [formMixin, validationMixin, modalComponentMixin],
    components: {
        SelectAsync,
        ButtonLoading,
        CCRange,
    },
    data() {
        return {
            formKey: 0,
            leadsSource: "manual",
            manualLeadIds: "",
            manualOverride: false,
            destinationType: "campaign",
            clientGroupOptions: [],
            forms: {
                sendLeadsToDeliveryCampaignWithTrollingDelivery: {
                    deliveryCampaignId: null,
                    clientGroupId: null,
                    timeZone: null,
                    maxConcurrency: 5,
                    minConcurrency: 1,
                    scheduleBias: 1,
                    _loading: false,
                },
            },
            inputHorizontal: {
                label: "col-sm-3",
                input: "col-sm-9",
            },
        };
    },
    props: {
        leads: {
            type: Array,
            required: true,
        },
        initialLeadIds: {
            type: String,
            default: "",
        },
    },
    validations() {
        return {
            forms: {
                sendLeadsToDeliveryCampaignWithTrollingDelivery: {
                    deliveryCampaignId: {
                        required: requiredIf(() => this.destinationType === "campaign"),
                    },
                    clientGroupId: {
                        required: requiredIf(() => this.destinationType === "balancer"),
                    },
                    timeZone: { required },
                    maxConcurrency: { required },
                    minConcurrency: { required },
                    scheduleBias: { required },
                },
            },
        };
    },

    computed: {
        parsedManualLeads() {
            if (!this.manualLeadIds.trim()) return [];
            return this.manualLeadIds
                .split(",")
                .map((s) => parseInt(s.trim()))
                .filter((n) => !isNaN(n));
        },

        parsedInitialLeads() {
            if (!this.initialLeadIds.trim()) return [];
            return this.initialLeadIds
                .split(",")
                .map((s) => parseInt(s.trim()))
                .filter((n) => !isNaN(n))
                .map((id) => ({ id }));
        },

        leadsForSubmit() {
            if (this.initialLeadIds && !this.manualOverride) {
                return this.parsedInitialLeads;
            }
            if (this.leadsSource === "selected") {
                return this.leads.map((lead) => ({
                    id: lead.id,
                    uuidSendingRef: lead.uuidSendingRef,
                }));
            }
            return this.parsedManualLeads.map((id) => ({ id }));
        },
    },

    watch: {
        show(val) {
            if (val) {
                // Reset todo antes de mostrar el modal
                this.resetForms();
                this.$v.forms.sendLeadsToDeliveryCampaignWithTrollingDelivery.$reset();
                this.forms.sendLeadsToDeliveryCampaignWithTrollingDelivery.maxConcurrency = 5;
                this.forms.sendLeadsToDeliveryCampaignWithTrollingDelivery.minConcurrency = 1;
                this.forms.sendLeadsToDeliveryCampaignWithTrollingDelivery.scheduleBias = 1;
                this.destinationType = "campaign";
                this.clientGroupOptions = [];
                this.manualOverride = false;
                this.manualLeadIds = "";
                this.leadsSource = this.leads.length > 0 ? "selected" : "manual";

                // Fuerza el remontaje del formulario para que SelectAsync y CCRange
                // partan de estado limpio (no mantienen estado interno entre aperturas)
                this.formKey++;
            }
            this.showComponent = val;
        },

        showComponent(val) {
            if (!val) {
                this.$v.forms.sendLeadsToDeliveryCampaignWithTrollingDelivery.$reset();
            }
        },

        destinationType(val) {
            if (val === "balancer" && this.clientGroupOptions.length === 0) {
                this.loadClientGroups();
            }
        },
    },

    methods: {
        async loadClientGroups() {
            try {
                const data = await this.fetcher({
                    url: "/shipment.balancing.api/client-groups",
                    method: "GET",
                });
                const items = data.items || data._items || data.data || [];
                this.clientGroupOptions = items.map((g) => ({
                    value: g.uuid,
                    text: g.name,
                    label: g.name,
                }));
            } catch (error) {
                console.error("[ThrottlingModal] Error loading client groups:", error);
            }
        },

        async submitForm(e, formName) {
            e.preventDefault();
            this.$v.forms.sendLeadsToDeliveryCampaignWithTrollingDelivery.$touch();

            try {
                if (this.$v.forms.sendLeadsToDeliveryCampaignWithTrollingDelivery.$invalid) {
                    throw { code: 422, message: this.$t("Invalid data") };
                }

                if (this.leadsForSubmit.length === 0) {
                    throw { code: 422, message: this.$t("No leads to send") };
                }

                this.forms.sendLeadsToDeliveryCampaignWithTrollingDelivery._loading = true;

                // Si destino = balanceador, validar leads antes de enviar
                if (this.destinationType === "balancer") {
                    const clientGroupId = this.forms[formName].clientGroupId;
                    const validation = await this.fetcher({
                        url: `/shipment.balancing.api/client-groups/${clientGroupId}/leads/validate`,
                        method: "POST",
                        data: { leads: this.leadsForSubmit.map((l) => l.id) },
                    });

                    if (!validation.valid) {
                        const missing = (validation.missing || []).join(", ");
                        throw {
                            code: 422,
                            message: `${this.$t("Some lead IDs were not found")}: ${missing}`,
                        };
                    }
                }

                const payload = {
                    leads: this.leadsForSubmit,
                    configuration: {
                        timeZone: this.forms[formName].timeZone,
                        maxConcurrency: this.forms[formName].maxConcurrency
                            ? parseInt(this.forms[formName].maxConcurrency)
                            : null,
                        minConcurrency: this.forms[formName].minConcurrency
                            ? parseInt(this.forms[formName].minConcurrency)
                            : null,
                        scheduleBias: this.forms[formName].scheduleBias
                            ? parseFloat(this.forms[formName].scheduleBias)
                            : 1,
                    },
                };

                if (this.destinationType === "campaign") {
                    payload.deliveryCampaignId = this.forms[formName].deliveryCampaignId;
                } else {
                    payload.clientGroupId = this.forms[formName].clientGroupId;
                }

                await this.fetcher({
                    url: "/throttling-service/throttling-deliveries",
                    method: "POST",
                    baseURL: process.env.VUE_APP_API_SASS,
                    data: payload,
                });

                this.showMessageSuccess();
                this.showComponent = false;
                this.$emit("submit", formName);
            } catch (error) {
                this.showMessageError(error);
            } finally {
                this.forms.sendLeadsToDeliveryCampaignWithTrollingDelivery._loading = false;
            }
        },

        resetForm(formName) {
            let form = this.getForm(formName);
            for (const key in form) {
                if (Object.hasOwnProperty.call(form, key)) {
                    if (typeof form[key] === "boolean") {
                        form[key] = false;
                    } else {
                        form[key] = null;
                    }
                }
            }
            return form;
        },
    },

};
</script>
